import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertTriangle } from 'lucide-react'
import type { MasterRunbook } from '../api'

interface MasterRunbookDeleteDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    masterRunbook: MasterRunbook | null
    onConfirm: () => void
    isLoading?: boolean
}

export function MasterRunbookDeleteDialog({
    open,
    onOpenChange,
    masterRunbook,
    onConfirm,
    isLoading = false,
}: MasterRunbookDeleteDialogProps) {
    const { t } = useTranslation()
    if (!masterRunbook) return null

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>{t('masterRunbooks.deleteMasterRunbook')}</DialogTitle>
                    <DialogDescription>
                        {/* Interpolated, not glued between fragments: word order differs by language. */}
                        {t('common.confirmDelete', { name: masterRunbook.description })} {t('masterRunbooks.deleteWarning')}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <Alert>
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                            {t('masterRunbooks.permanentAction')}
                        </AlertDescription>
                    </Alert>

                    <div className="bg-muted p-3 rounded-lg">
                        <div className="text-sm space-y-1">
                            <div><strong>{t('masterRunbooks.description')}:</strong> {masterRunbook.description}</div>
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isLoading}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={onConfirm}
                        disabled={isLoading}
                    >
                        {isLoading ? t('masterRunbooks.deleting') : t('masterRunbooks.deleteMasterRunbook')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
